import clsx from 'clsx';
import React, { Suspense, lazy } from 'react';

import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ContentType, Page } from '@tracking';

import { AudioProvider } from './AudioContext';
import Data from './Layouts/Data';
import Feature from './Layouts/Feature';
import Graphic from './Layouts/Graphic';
import LockedArticle from './Layouts/LockedArticle';
import Standard from './Layouts/Standard';
import Weekend from './Layouts/Weekend';
import { type ArticleProps, ArticleTheme, type ThemeViewsProps } from './interfaces';

const Paywall = lazy(() => import('./Paywall'));

const getArticleTemplate = (theme: string) => {
  switch (theme) {
    case ArticleTheme.Feature:
      return Feature;
    case ArticleTheme.Graphic:
      return Graphic;
    case ArticleTheme.Weekend:
      return Weekend;
    case ArticleTheme.Data:
      return Data;
    default:
      return Standard;
  }
};

const ArticleLayout = ({
  article,
  canShareArticle,
  env,
  hideAwarenessBar,
  isSEOBotAndArticleIsNotFree,
  isWebview,
  midArticleUnitType,
  mostPopularArticles,
  newsletterId,
  relatedArticles,
  relatedEvent,
  theme,
}: ArticleProps) => {
  // Canonical access signal: server sends fullText when the viewer may read the full article
  // (subscriber, detected bot, unlocked token, free article). null means locked.
  const canViewFullArticle = article.fullText !== null;
  const isLocked = !canViewFullArticle;

  const themeProps: ThemeViewsProps = {
    article,
    canShareArticle,
    canViewFullArticle,
    env,
    isSEOBotAndArticleIsNotFree,
    isWebview,
    midArticleUnitType,
    mostPopularArticles,
    newsletterId,
    relatedArticles,
    relatedEvent,
  };

  const ArticleTemplate = getArticleTemplate(theme);
  const hydrated = useServerSideRendering();
  const { currentUser } = useCurrentUser();
  const { Track } = useEventTracking({
    contentId: article.id,
    contentType: ContentType.Article,
    page: Page.Article,
    pageId: article.id,
  });

  // Read URL params only after hydration — window.location is not available during SSR.
  const offerCode = hydrated
    ? new URLSearchParams(window.location.search).get('offer') ?? undefined
    : undefined;
  const shareGuid = hydrated
    ? new URLSearchParams(window.location.search).get('shared') ?? undefined
    : undefined;

  // Non-pro subscribers reading a pro article see a sticky upsell paywall instead of the
  // full-bleed LockedArticle. This check is post-hydration only (currentUser starts null).
  const shouldShowStickyPaywall =
    isLocked && article.isPro && !!currentUser?.is_subscriber && !currentUser?.is_pro_subscriber;

  return (
    <AudioProvider>
      <Track>
        <article
          className={clsx('relative', {
            'pt-32 sm:pt-48': !isWebview && !isLocked,
            'pt-90 sm:pt-[140px]':
              !isWebview && isLocked && (shouldShowStickyPaywall || hideAwarenessBar),
            'pt-[155px] sm:pt-[140px]':
              !isWebview && isLocked && !shouldShowStickyPaywall && !hideAwarenessBar,
          })}
        >
          {/* SSR always renders ArticleTemplate — no user-specific switching in the server pass.
              Non-subscribers see freeBlurb only (fullText is null). LockedArticle appears
              post-hydration so the server output is identical for all non-subscribers. */}
          <ArticleTemplate {...themeProps} />
          {hydrated && isLocked && !isWebview && !shouldShowStickyPaywall && (
            <LockedArticle offerCode={offerCode} shareGuid={shareGuid} {...themeProps} />
          )}
        </article>
        {hydrated && shouldShowStickyPaywall && (
          <Suspense>
            <div
              className={clsx('sticky -bottom-px z-50 mt-0 w-full bg-grayscale-charcoal')}
              data-testid="paywall-container"
            >
              <Paywall
                articleId={article.id}
                isTwoOptPaywall
                offerCode={offerCode}
                shareGuid={shareGuid}
              />
            </div>
          </Suspense>
        )}
      </Track>
    </AudioProvider>
  );
};

export default ArticleLayout;
